import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ArrowRight, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { sendWebhook } from '../utils/webhook';
import type { FormData } from '../types';
import SubmissionPopup from '../components/SubmissionPopup';
import MouseFollowEffect from '../components/MouseFollowEffect';

const BrandLogos = () => {
  const [category, setCategory] = useState<'fintech' | 'ecommerce'>('fintech');
  const navigate = useNavigate();

  const logos = {
    fintech: [
      { name: "Zilch", className: "text-3xl" },
      { name: "Smart", className: "text-3xl" },
      { name: "Wayflyer", className: "text-3xl" },
      { name: "Green-Got", className: "text-3xl" },
      { name: "Mangopay", className: "text-3xl" }
    ],
    ecommerce: [
      { name: "Frankster", className: "text-3xl" },
      { name: "BonnieYoung", className: "text-3xl" },
      { name: "Bacares", className: "text-3xl" },
      { name: "Huffer", className: "text-3xl" },
      { name: "Mooks", className: "text-3xl" },
      { name: "Cuyana", className: "text-3xl" },
      { name: "Culture Kings", className: "text-3xl" }
    ]
  };

  const handleJoinList = () => {
    navigate('/contact', { state: { category } });
  };

  return (
    <section className="py-8 bg-gradient-to-b from-black/30 to-transparent">
      <div className="max-w-7xl mx-auto px-4">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl font-bold text-center mb-6 float-slow"
        >
          We've helped great brands reach new heights.
        </motion.h2>

        <div className="flex justify-center gap-8 mb-8">
          {['fintech', 'ecommerce'].map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat as typeof category)}
              className={`px-6 py-3 rounded-full text-lg font-medium transition-all ${
                category === cat 
                  ? 'bg-golden text-white float-medium'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
              aria-label={`View ${cat} clients`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        <MouseFollowEffect className="bg-black/20 border-none p-8 rounded-2xl mb-8">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center justify-items-center"
          >
            {logos[category].map((logo, index) => (
              <motion.div
                key={logo.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className={`flex items-center justify-center p-4 hover:scale-110 transition-all duration-300 float-${index % 2 === 0 ? 'slow' : 'medium'}`}
              >
                <span className={`${logo.className} font-bold bg-gradient-to-r from-golden to-white bg-clip-text text-transparent hover:from-white hover:to-golden transition-all duration-300 text-center`}>
                  {logo.name}
                </span>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: logos[category].length * 0.1 }}
              className="flex items-center justify-center p-4"
            >
              <button
                onClick={handleJoinList}
                className="group relative px-6 py-3 bg-transparent border-2 border-golden rounded-full text-golden hover:bg-golden hover:text-white transition-all duration-300 transform hover:scale-110 float-fast"
                aria-label="Join our client list"
              >
                <span className="relative z-10">Join This List</span>
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-golden/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </motion.div>
          </motion.div>
        </MouseFollowEffect>
      </div>
    </section>
  );
};

function Home() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showGuidePopup, setShowGuidePopup] = useState(false);

  const handleSubmit = async (e: React.FormEvent, type: FormData['type'] = 'newsletter') => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData: FormData = {
      email,
      type,
      timestamp: new Date().toISOString(),
      source: 'homepage'
    };

    try {
      await sendWebhook(formData);
      setEmail('');
      setShowPopup(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGuideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    const formData: FormData = {
      email,
      type: 'guide',
      timestamp: new Date().toISOString(),
      source: 'homepage',
      guide: {
        title: '4 Simple Steps To Quickly Attract More Clients For Your Business Using Meta Ads',
        description: 'A comprehensive guide to Meta advertising success'
      }
    };

    try {
      await sendWebhook(formData);
      setEmail('');
      setShowGuidePopup(true);
      
      // Trigger the download after a short delay
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = '/files/Meta-Guide By DS Marketing.pdf';
        link.download = 'Meta-Guide By DS Marketing.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 1500);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStrategySession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    const formData: FormData = {
      email,
      type: 'consultation',
      timestamp: new Date().toISOString(),
      source: 'homepage'
    };

    try {
      await sendWebhook(formData);
      window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/ds-marketing-meeting';
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <spline-viewer url="https://prod.spline.design/MFrkoY3O-wbs5O2Z/scene.splinecode" loading="lazy"></spline-viewer>
        </div>
        
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        
        <div className="relative z-20 text-center max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <motion.h1 
              className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              More Growth. More Clients.
              <motion.span 
                className="text-3xl md:text-4xl block mt-4 text-golden drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Guaranteed Results
              </motion.span>
            </motion.h1>
            <motion.div 
              className="flex flex-col items-center justify-center space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <Link 
                to="/schedule" 
                className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-110 hover:bg-golden-600"
                aria-label="Book a free strategy call"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Strategy Call
              </Link>
              <motion.p 
                className="text-xl text-white font-medium mt-6 bg-black/70 backdrop-blur-sm py-3 px-6 rounded-full inline-block border border-golden/20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.0 }}
              >
                Don't wait while your competitors capture your market share.{' '}
                <span className="text-golden font-bold">Book your free strategy call now!</span>
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <section className="py-8 bg-black/90">
        <div className="max-w-7xl mx-auto px-4">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-6 float-slow"
          >
            Our <span className="text-golden">Record</span>
          </motion.h2>
          <MouseFollowEffect className="bg-black/20 border-none p-6 rounded-2xl mb-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center float-medium"
              >
                <h3 className="text-4xl font-bold text-golden mb-2">10M+</h3>
                <p className="text-gray-300">Content Views</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-center float-slow"
              >
                <h3 className="text-4xl font-bold text-golden mb-2">5+</h3>
                <p className="text-gray-300">Years of Experience</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-center float-fast"
              >
                <h3 className="text-4xl font-bold text-golden mb-2">150+</h3>
                <p className="text-gray-300">Projects Completed</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-center float-medium"
              >
                <h3 className="text-4xl font-bold text-golden mb-2">93%</h3>
                <p className="text-gray-300">Client Satisfaction</p>
              </motion.div>
            </div>
          </MouseFollowEffect>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Link
                to="/services"
                className="inline-flex items-center justify-center w-full px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-110 float-medium"
                aria-label="Explore our marketing services"
              >
                Explore Our Services
              </Link>
              <p className="mt-2 text-gray-300">
                Discover how we can transform your brand
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Link
                to="/schedule"
                className="inline-flex items-center justify-center w-full px-8 py-4 border-2 border-golden text-golden rounded-full text-lg font-semibold hover:bg-golden hover:text-white transition-all duration-300 transform hover:scale-110 float-medium"
                aria-label="Book a strategy call"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Strategy Call
              </Link>
              <p className="mt-2 text-gray-300">
                Get personalized marketing advice
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-dark-gradient">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h3 className="text-2xl font-bold mb-4 text-golden">The Challenge</h3>
              <p className="text-gray-300 text-lg">
                Are you struggling to engage your website visitors and turn them into loyal customers? Every day, businesses lose potential leads because their content fails to resonate and capture attention.
              </p>
            </div>

            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h3 className="text-2xl font-bold mb-4 text-golden">The Risk</h3>
              <p className="text-gray-300 text-lg">
                Without compelling storytelling, your brand risks being overshadowed by competitors who are already connecting with their audiences. The digital landscape becomes more competitive each day, and standing out becomes increasingly challenging.
              </p>
            </div>

            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h3 className="text-2xl font-bold mb-4 text-golden">The Solution</h3>
              <p className="text-gray-300 text-lg mb-6">
                Imagine if every piece of content you published sparked genuine interest and drove conversions effortlessly. Our expert services transform your brand's message into captivating narratives that not only engage but also convert—giving you the edge in today's crowded digital landscape.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-110"
                aria-label="Learn about our marketing services"
              >
                Explore Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-8 bg-black/30">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-3xl md:text-4xl font-bold mb-6 text-center">
            Our <span className="text-golden">Offer</span>
          </h3>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h4 className="text-xl font-bold mb-4 text-golden">Our Promise</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  Say goodbye to invisible content - we'll get you noticed online
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  No more hit-or-miss content - everything we create converts
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  Skip the freelancer headaches and agency markups
                </li>
              </ul>
            </div>

            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h4 className="text-xl font-bold mb-4 text-golden">What You Get</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  A content strategy that's all about your brand
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  Eye-catching content that makes people stop scrolling
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  A clear game plan to keep your content machine running
                </li>
              </ul>
            </div>

            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h4 className="text-xl font-bold mb-4 text-golden">Why We're Different</h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  We mix creativity with real data (no guesswork here!)
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  Every piece of content has one job: getting you results
                </li>
                <li className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  We know your industry inside out
                </li>
              </ul>
             </div>

            <div className="bg-black/30 p-8 rounded-2xl border border-gray-800">
              <h4 className="text-xl font-bold mb-4 text-golden">Zero Risk</h4>
              <div className="space-y-3 text-gray-300">
                <p className="flex items-start">
                  <span className="text-golden mr-2">✓</span>
                  Not happy? Get your money back within 30 days
                </p>
                <p className="text-sm mt-2 text-gray-400">
                  If you don't see real improvement in your engagement and conversions, 
                  we'll refund every penny. Simple as that.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-4">
            <p className="text-xl text-gray-300 mb-4">
              Ready to stop blending in and start standing out?
            </p>
            <p className="text-lg text-golden font-semibold">
              Let's make your content work harder for you
            </p>
          </div>
          
          <div className="max-w-md mx-auto mb-8">
            <form onSubmit={handleStrategySession} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-6 py-4 rounded-full bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white transition-all duration-300 hover:border-golden/50"
                required
                disabled={isSubmitting}
                aria-label="Your email address"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold transition-all duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-golden-600'
                }`}
                aria-label="Schedule your free strategy session"
              >
                {isSubmitting ? 'Scheduling...' : 'Schedule Your Free Strategy Session'}
              </motion.button>
            </form>
          </div>
        </div>
      </section>

      <BrandLogos />

      <section className="py-8 bg-dark-gradient">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4"
            >
              <p className="text-base md:text-lg font-semibold text-golden mb-4 tracking-wider">
                -- FREE GUIDE --
              </p>
              <h2 className="text-4xl font-bold mb-2">
                4 Simple Steps To
              </h2>
              <h2 className="text-3xl font-bold mb-2">
                Quickly Attract More Clients
              </h2>
              <h2 className="text-2xl font-bold text-golden">
                For Your Business Using Meta Ads
              </h2>
            </motion.div>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-300"
            >
              Get instant access to our proven Meta Ads framework
            </motion.p>
          </div>
          <motion.form 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleGuideSubmit}
            className="space-y-4"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-full bg-black/50 border-2 border-gray-800 focus:outline-none focus:border-golden text-white transition-all duration-300 hover:border-golden/50"
                required
                disabled={isSubmitting}
                aria-label="Your email for the free guide"
              />
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-4 bg-golden text-white rounded-full font-semibold transition-all duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-golden-600'
                }`}
                aria-label="Download free Meta Ads guide"
              >
                {isSubmitting ? 'Sending Guide...' : 'Get Your Free Guide Now'}
              </motion.button>
            </div>
            <p className="text-center text-sm text-gray-400">
              Instant access to proven strategies that have helped our clients achieve 3x ROAS with Meta Ads
            </p>
          </motion.form>
        </div>
      </section>

      <SubmissionPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
      <SubmissionPopup 
        isOpen={showGuidePopup} 
        onClose={() => setShowGuidePopup(false)}
        message="Your guide will start downloading shortly!"
      />
    </div>
  );
}

export default Home;